/**
 * Ajusta uma data para o último dia válido do mês, caso o dia especificado não exista
 * @param ano - Ano da data
 * @param mes - Mês (1-12)
 * @param dia - Dia desejado (1-31)
 * @returns Date válida ajustada para o último dia do mês se necessário
 */
export function ajustarDataValida(ano: number, mes: number, dia: number): Date {
  // Obter o último dia do mês
  const ultimoDiaDoMes = new Date(ano, mes, 0).getDate();
  
  // Ajustar o dia se for maior que o último dia válido
  const diaCorrigido = Math.min(dia, ultimoDiaDoMes);
  
  // Retornar a data ajustada (mes - 1 porque Date usa 0-11 para meses)
  return new Date(ano, mes - 1, diaCorrigido);
}

/**
 * Formata uma data para string no formato yyyy-MM-dd com dia ajustado
 */
export function formatarDataSQL(ano: number, mes: number, dia: number): string {
  const dataAjustada = ajustarDataValida(ano, mes, dia);
  const y = dataAjustada.getFullYear();
  const m = String(dataAjustada.getMonth() + 1).padStart(2, '0');
  const d = String(dataAjustada.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Verifica se uma data foi ajustada e retorna mensagem de aviso
 */
export function verificarAjusteData(ano: number, mes: number, diaOriginal: number): {
  foiAjustado: boolean;
  diaFinal: number;
  mensagem?: string;
} {
  const ultimoDia = new Date(ano, mes, 0).getDate();
  const foiAjustado = diaOriginal > ultimoDia;
  
  return {
    foiAjustado,
    diaFinal: Math.min(diaOriginal, ultimoDia),
    mensagem: foiAjustado 
      ? `O dia ${diaOriginal} foi ajustado para ${Math.min(diaOriginal, ultimoDia)} (último dia de ${getNomeMes(mes)}/${ano})`
      : undefined
  };
}

export function getNomeMes(mes: number): string {
  const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return meses[mes - 1] || 'Mês inválido';
}

/**
 * Calcula o status de uma fatura de cartão de crédito
 */
export type InvoiceStatus = 'paga' | 'aberta' | 'fechada' | 'parcial';

export function getInvoiceStatus(
  invoice: { paga: boolean; mes_referencia: number; ano_referencia: number },
  card: { dia_fechamento?: number | null },
  currentDate: Date = new Date()
): InvoiceStatus {
  if (invoice.paga) return 'paga';

  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const currentDay = currentDate.getDate();

  // Fatura Futura (Parcial)
  if (invoice.ano_referencia > currentYear || (invoice.ano_referencia === currentYear && invoice.mes_referencia > currentMonth)) {
    return 'parcial';
  }

  // Fatura Passada (Não paga)
  if (invoice.ano_referencia < currentYear || (invoice.ano_referencia === currentYear && invoice.mes_referencia < currentMonth)) {
    return 'fechada';
  }

  // Mês Atual
  if (card.dia_fechamento && currentDay >= card.dia_fechamento) {
    return 'fechada';
  }

  return 'aberta';
}
